/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2026
///                                     RXO Inc.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of RXO Inc. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useState } from "react";
import { useIntl } from "react-intl";

import Modal from "./Modal";
import OutlinedInput from "./form-controls/OutlinedInput";
import Spinner from "./icons/Spinner";
import { getLocations } from "../api/locations";
import messages from "../helpers/messages";

export const formatCell = value => (value == null ? "" : String(value));

const NA_COUNTRY_CODE_DISPLAY = { US: "USA", MX: "MEX", CA: "CAN" };

export const formatLocationCountryCode = value => {
  if (value == null) {
    return "";
  }
  const trimmed = String(value).trim();
  if (!trimmed) {
    return "";
  }
  const upper = trimmed.toUpperCase();
  if (upper === "USA" || upper === "MEX" || upper === "CAN") {
    return upper;
  }
  return NA_COUNTRY_CODE_DISPLAY[upper] ?? formatCell(value);
};

export const formatLocationStateCode = value => {
  if (value == null) {
    return "";
  }
  const lettersOnly = String(value).trim().replace(/[^A-Za-z]/g, "");
  if (!lettersOnly) {
    return "";
  }
  return lettersOnly.toUpperCase();
};

const LOCATION_FIELDS = ["cityName", "stateCode", "postalCode", "countryCode2"];

const COLUMN_MESSAGE_KEYS = {
  cityName: messages.cityName,
  stateCode: messages.stateCode,
  postalCode: messages.postalCode,
  countryCode2: messages.countryCode2
};

const isSearchTermValid = searchTerm => searchTerm.trim().length > 2;

export const LocationSearchModal = ({ open, handleClose }) => {
  const intl = useIntl();
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [showMinLengthHint, setShowMinLengthHint] = useState(false);

  const trimmedSearchTerm = searchTerm.trim();
  const canSearch = isSearchTermValid(searchTerm) && !isLoading;

  const handleSearch = () => {
    if (!isSearchTermValid(searchTerm)) {
      setShowMinLengthHint(true);
      return;
    }

    setShowMinLengthHint(false);
    setIsLoading(true);
    setError(null);

    getLocations(trimmedSearchTerm)
      .then(({ data }) => {
        setResults(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        if (err?.message === "Cancelled By User") {
          return;
        }
        setResults([]);
        setError(messages.unableToRetrieveData.defaultMessage);
      })
      .finally(() => {
        setIsLoading(false);
        setHasSearched(true);
      });
  };

  const handleKeyDown = event => {
    if (event.key === "Enter" && canSearch) {
      event.preventDefault();
      handleSearch();
    }
  };

  return (
    <Modal
      open={open}
      handleClose={handleClose}
      title={messages.locations.defaultMessage}
      xLarge={true}
      modalStyle={{ width: "860px" }}
      bodyStyle={{ maxHeight: "70vh", overflow: "auto" }}
      bodyClass="border-top-grey px-24 pt-24 pb-24"
      titleClass="modal__title_light uppercase"
      closeButtonClass="modal__close-button mr-24 mt-16"
    >
      <div className="flex items-start">
        <div className="flex-auto mr-12">
          <OutlinedInput
            data-testid="location-search-input"
            value={searchTerm}
            onChange={({ target }) => {
              setSearchTerm(target.value);
              if (isSearchTermValid(target.value)) {
                setShowMinLengthHint(false);
              }
            }}
            onKeyDown={handleKeyDown}
            label={messages.locationSearchPlaceholder.defaultMessage}
          />
          {showMinLengthHint && (
            <p
              className="text-red-failure-dark font-size-12 mt-4"
              data-testid="location-search-min-length"
            >
              {messages.locationSearchMinLength.defaultMessage}
            </p>
          )}
        </div>
        <button
          type="button"
          data-testid="location-search-submit"
          className="h-32 text-button univers-67-bold-cond mt-6 short-button"
          disabled={!canSearch}
          onClick={handleSearch}
        >
          {messages.search.defaultMessage}
        </button>
      </div>

      {isLoading && (
        <div className="flex justify-center py-16" data-testid="location-search-loading">
          <Spinner
            className="spinner"
            height="32px"
            width="32px"
            fill="#C9CACC"
          />
        </div>
      )}

      {!isLoading && error && (
        <p
          className="text-red-failure-dark font-size-14 mt-16"
          data-testid="location-search-error"
        >
          {error}
        </p>
      )}

      {!isLoading &&
        !error &&
        hasSearched &&
        results &&
        results.length === 0 && (
          <p className="font-size-14 mt-16" data-testid="location-search-empty">
            {messages.noResultsFound.defaultMessage}
          </p>
        )}

      {!isLoading && results && results.length > 0 && (
        <>
          <p className="font-size-14 mt-16 mb-8" data-testid="location-search-count">
            {intl.formatMessage(messages.locationSearchResultCount, {
              count: results.length
            })}
          </p>
          <div className="overflow-auto">
            <table
              className="card display-table one-whole"
              data-testid="location-search-results"
            >
              <thead>
                <tr className="row">
                  {LOCATION_FIELDS.map(field => (
                    <th key={field} className="py-12 pl-12 pr-8 font-size-12">
                      {COLUMN_MESSAGE_KEYS[field].defaultMessage}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {results.map((row, index) => (
                  <tr key={row.cityId ?? index} className="row">
                    {LOCATION_FIELDS.map(field => (
                      <td key={field} className="py-8 pl-12 pr-8 font-size-12">
                        {field === "countryCode2"
                          ? formatLocationCountryCode(row[field])
                          : field === "stateCode"
                          ? formatLocationStateCode(row[field])
                          : formatCell(row[field])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
};

export default LocationSearchModal;
